import { QuotesIcon, TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  accentedWeights,
  componentKey,
  emphasisDirection,
  emphasisKey,
  type ScoreComponent,
  type WeightPreview,
} from '@entities/search-session';

import styles from './Criteria.module.css';

interface WeightsBlockProps {
  weights: readonly WeightPreview[];
  /** `full` дополнительно показывает неизменённые компоненты (экран FE-073). */
  variant?: 'compact' | 'full' | undefined;
  disabled: boolean;
  /** Снятие акцента создаёт новую версию критериев (MVP_PRD §11). */
  onReset: (component: ScoreComponent) => void;
  resetting: ScoreComponent | null;
}

/**
 * Блок «Что для вас важнее» (FE-071, MVP_PRD §11).
 *
 * Показывается только при наличии акцентов: без них действуют базовые веса, и
 * перечислять их пользователю незачем. Числа — `configured_weight` и
 * `share_percent` — приходят с backend посчитанными; фронт ничего не умножает
 * и не нормализует (CLAUDE.md §3 п. 4).
 *
 * У каждого акцента показана цитата, из которой он выведен: акцент существует
 * только вместе с явной фразой пользователя (MATCHING §4.2). Поэтому акцент
 * можно **снять**, но нельзя назначить руками — кнопки «сделать критичным» тут
 * нет и быть не может.
 */
export function WeightsBlock({
  weights,
  variant = 'compact',
  disabled,
  onReset,
  resetting,
}: WeightsBlockProps) {
  const { t } = useTranslation();
  const titleId = useId();

  const accented = accentedWeights(weights);
  if (accented.length === 0) return null;

  const rest = variant === 'full' ? weights.filter((item) => item.level === 'normal') : [];

  return (
    <section className={styles.weights} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.weightsTitle}>
        {t('criteria.weights.title')}
      </h3>
      <p className={styles.weightsLead}>{t('criteria.weights.lead')}</p>

      <ul className={styles.weightsList}>
        {accented.map((item) => {
          const direction = emphasisDirection(item.level);
          const DirectionIcon = direction === 'down' ? TrendDownIcon : TrendUpIcon;
          return (
            <li key={item.component} className={styles.weight}>
              <div className={styles.weightHead}>
                <span className={styles.weightName}>{t(componentKey(item.component))}</span>
                <span
                  className={[
                    styles.weightLevel,
                    direction === 'down' ? styles.weightLevelDown : styles.weightLevelUp,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <DirectionIcon size={12} weight="bold" aria-hidden="true" />
                  {t(emphasisKey(item.level))}
                </span>
                <span className={`${styles.weightShare} tabular`}>
                  {t('criteria.weights.share', { percent: item.share_percent })}
                </span>
              </div>

              <div
                className={styles.weightBar}
                role="img"
                aria-label={t('criteria.weights.barLabel', {
                  component: t(componentKey(item.component)),
                  percent: item.share_percent,
                  base: item.base_weight,
                })}
              >
                {/* Базовый вес отмечен риской: видно, насколько акцент сдвинул
                    компонент относительно модели по умолчанию. */}
                <span
                  className={styles.weightBase}
                  style={{ insetInlineStart: `${String(item.base_weight)}%` }}
                />
                <span
                  className={styles.weightFill}
                  style={{ inlineSize: `${String(Math.min(item.share_percent, 100))}%` }}
                />
              </div>

              {item.source_text ? (
                <blockquote className={styles.quote}>
                  <QuotesIcon size={14} weight="fill" aria-hidden="true" />
                  {item.source_text}
                </blockquote>
              ) : null}

              <button
                type="button"
                className={styles.weightReset}
                disabled={disabled}
                onClick={() => {
                  onReset(item.component);
                }}
              >
                {resetting === item.component
                  ? t('criteria.weights.resetting')
                  : t('criteria.weights.reset')}
              </button>
            </li>
          );
        })}
      </ul>

      {rest.length > 0 ? (
        <div className={styles.weightsRest}>
          <p className={styles.weightsRestTitle}>{t('criteria.weights.restTitle')}</p>
          <ul className={styles.weightsRestList}>
            {rest.map((item) => (
              <li key={item.component}>
                <span>{t(componentKey(item.component))}</span>
                <span className="tabular">
                  {t('criteria.weights.share', { percent: item.share_percent })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
